import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Settings, APP_CONFIG } from 'src/app/config/settings';
import { combine } from 'src/app/shared/utilities/route';
import { FetchArgs, PagingTokenFixed } from 'src/app/shared/models/core';
import { TicketGridItem } from 'src/app/travelx/tickets/ticket-grid-item';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { generateQuery } from 'src/app/shared/utilities/grid-query-params';
import { TicketCounsel } from 'src/app/travelx/models/ticket-counsel.model';
import { TicketDetails } from 'src/app/travelx/models/ticket-details.model';
import { TicketHistory } from 'src/app/travelx/models/ticket-history.model';
import { TicketEventsEnum } from 'src/app/travelx/enums/ticket-events.enum';
import { TicketTaskItem } from 'src/app/travelx/models/ticket-task-item.model';
import { map } from 'rxjs/operators';
import { TicketTaskItemNoteHistory } from 'src/app/travelx/models/ticket-task-item-note-history.model';
import { TicketEmployee } from 'src/app/travelx/models/ticket-employee.model';
import { TicketCategory } from 'src/app/travelx/models/ticket-category.model';
import { CountryState } from 'src/app/shared/models/lookup-country-states.model';
import { TicketUrgency } from 'src/app/travelx/models/ticket-urgency.model';
import { TicketCandidateContract } from 'src/app/travelx/models/ticket-candidate-contract.model';
import { TicketContract } from 'src/app/travelx/models/ticket-contract.model';
import { TicketContractLookups } from 'src/app/travelx/models/ticket-contract-lookups.model';
import { TaskUpdateRequest } from 'src/app/travelx/models/task-update-request.model';
import { TicketAttachments } from 'src/app/travelx/models/ticket-attachments.model';
import { WINDOW } from 'src/app/core/core.module';
import { TicketFilesUpload } from 'src/app/travelx/models/ticket-files-upload.model';
import { TicketCandidate } from 'src/app/travelx/models/ticket-candidate.model';
import { TicketCreate } from 'src/app/travelx/models/ticket-create.model';
import { TicketSettingsCategoryType } from '../models/ticket-settings-category-type.model';
import { TravelContract } from 'src/app/travel-contracts/travel-contracts-grid/models/travel-contract';
import { TicketGridPermissions } from 'src/app/travelx/tickets/ticket-grid-permissions';
import { IdentityService } from 'src/app/core/identity.service';
import { OptionModel } from 'src/app/shared/grid/filters/option.model';
import { TicketTagType } from 'src/app/travelx/models/ticket-tag-type.model';
import { StaffingAdvisoryFromTicketRequest } from 'src/app/travelx/models/staffing-advisory-from-ticket-request.model';
import { KeyValuePair } from 'src/app/shared/models/key-value-pair.model';

@Injectable({
    providedIn: 'root'
})
export class TicketService {
    private readonly _coreApiUrl: string;
    private readonly _coreApiPath: string;

    private _authorizedForResolve = false;
    private _authorizedForReassign = false;
    private _authorizedForAdd = false;
    private _authorizedForSettings = false;

    private readonly _ticketTagTypesUrl = 'ayanova/travelx/ticket-tag-types';

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(WINDOW) private readonly _window: Window,
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _identityService: IdentityService
    ) {
        this._coreApiUrl = this._settings.coreAPIUrl;
        this._coreApiPath = combine(this._settings.coreAPIUrl, 'AyaNova/travelx/tickets');

        this._authorizedForResolve = this._identityService.authorize('travelxadmin.ticket-resolve', 'r');
        this._authorizedForReassign = this._identityService.authorize('travelxadmin.ticket-resolve', 'r');
        this._authorizedForAdd = this._identityService.authorize('travelx.tickets', 'c');
        this._authorizedForSettings = this._identityService.hasPermission('TravelxAdmin', 'Ticket-resolve');
    }

    search(args: FetchArgs): Observable<PagingTokenFixed<TicketGridItem[]>> {
        const params = generateQuery(args);
        const url = `${this._coreApiPath}/search`;
        return this._httpClient.get<PagingTokenFixed<TicketGridItem[]>>(url, { params });
    }

    getTicketDetails(ticketId: number): Observable<TicketDetails> {
        return this._httpClient.get<TicketDetails>(`${this._coreApiPath}/grid-items/${ticketId}`);
    }

    getBrands(): Observable<Lookup[]> {
        const url = combine(this._coreApiUrl, 'AyaNova/facility-contracts/system/brands');
        return this._httpClient.get<Lookup[]>(url);
    }

    updateTicketDescription(ticketId: number, note: string): Observable<unknown> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        };
        return this._httpClient.put(`${this._coreApiPath}/${ticketId}/note`, JSON.stringify(note), httpOptions);
    }

    updateTicketBookedDays(ticketId: number, description: string): Observable<unknown> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        };
        return this._httpClient.put(
            `${this._coreApiPath}/${ticketId}/booked-days`,
            JSON.stringify(description),
            httpOptions
        );
    }

    updateTicketCounsel(ticketId: number, counselId: number): Observable<unknown> {
        const counselUpdate = {
            counselId
        };

        return this._httpClient.put(`${this._coreApiPath}/${ticketId}`, counselUpdate);
    }

    getCounsels(): Observable<TicketCounsel[]> {
        return this._httpClient.get<TicketCounsel[]>(`${this._coreApiPath}/counsels`);
    }

    updateWorkersCompCoordinator(ticketId: number, workersCompCoordinatorId: number): Observable<unknown> {
        const workersCompCoordinatorUpdate = {
            workersCompCoordinatorId
        };

        return this._httpClient.put(`${this._coreApiPath}/${ticketId}`, workersCompCoordinatorUpdate);
    }

    getWorkersCompCoordinators(): Observable<KeyValuePair<number, string>[]> {
        return this._httpClient.get<KeyValuePair<number, string>[]>(`${this._coreApiPath}/workers-comp-coordinators`);
    }

    getTicketHistory(ticketId: number): Observable<TicketHistory[]> {
        return this._httpClient.get<TicketHistory[]>(`${this._coreApiPath}/${ticketId}/history`);
    }

    getTicketTypes(): Observable<OptionModel[]> {
        return this._httpClient.get<OptionModel[]>(`${this._coreApiPath}/ticket-types`);
    }

    getTicketCategories(): Observable<TicketCategory[]> {
        return this._httpClient.get<TicketCategory[]>(`${this._coreApiPath}/categories`);
    }

    closeTicket(id: number, data: unknown): Observable<unknown> {
        return this._httpClient.put(`${this._coreApiPath}/${id}/close`, data);
    }

    reopenTicket(id: number): Observable<unknown> {
        return this._httpClient.put(`${this._coreApiPath}/${id}/reopen`, null);
    }

    updateTicket(id: number, data: unknown): Observable<unknown> {
        return this._httpClient.put(`${this._coreApiPath}/${id}`, data);
    }

    createTicketHistory(ticketId: number, comment: string): Observable<unknown> {
        const data = {
            eventId: TicketEventsEnum.LeftComment,
            comment
        };

        return this._httpClient.post<unknown>(`${this._coreApiPath}/${ticketId}/history`, data);
    }

    getTicketTasks(ticketId: number): Observable<TicketTaskItem[]> {
        return this._httpClient.get<TicketTaskItem[]>(`${this._coreApiPath}/${ticketId}/tasks`).pipe(
            map((tasks) => {
                tasks = tasks.sort((first, second) => first.id - second.id);

                return tasks;
            })
        );
    }

    getContractById(contractId: number): Observable<TicketContract> {
        return this._httpClient.get<TicketContract>(`${this._coreApiUrl}/ayanova/contracts/id/${contractId}`);
    }

    getContractDetails(contractId: string): Observable<TravelContract> {
        const url = `${this._coreApiUrl}/ayanova/candidate/contracts/${contractId}`;
        return this._httpClient.get<TravelContract>(url);
    }

    getLookups(): Observable<TicketContractLookups> {
        return this._httpClient.get<TicketContractLookups>(`${this._coreApiUrl}/ayanova/contracts/lookups`);
    }

    getCountryStates(): Observable<CountryState[]> {
        return this._httpClient.get<CountryState[]>(`${this._coreApiUrl}/ayanova/states`);
    }

    getTicketUrgency(): Observable<TicketUrgency[]> {
        return this._httpClient.get<TicketUrgency[]>(`${this._coreApiPath}/urgencies`);
    }

    getTicketTagTypes(): Observable<TicketTagType[]> {
        const url = combine(this._coreApiUrl, this._ticketTagTypesUrl);
        return this._httpClient.get<TicketTagType[]>(url);
    }

    getTicketTagTypesForCategory(categoryId: number): Observable<TicketTagType[]> {
        const url = combine(this._coreApiUrl, this._ticketTagTypesUrl, `${categoryId}`);
        return this._httpClient.get<TicketTagType[]>(url);
    }

    addTicketTagType(tagType: TicketTagType): Observable<number> {
        const url = combine(this._coreApiUrl, this._ticketTagTypesUrl);
        return this._httpClient.post<number>(url, tagType);
    }

    updateTicketTagType(tagType: TicketTagType): Observable<unknown> {
        const url = combine(this._coreApiUrl, this._ticketTagTypesUrl, `${tagType.id}`);
        return this._httpClient.put<unknown>(url, tagType);
    }

    deleteTicketTagType(id: number): Observable<unknown> {
        const url = combine(this._coreApiUrl, this._ticketTagTypesUrl, `${id}`);
        return this._httpClient.delete<unknown>(url);
    }

    validateTicketTagTypeName(id: number, name: string): Observable<boolean> {
        const url = combine(this._coreApiUrl, this._ticketTagTypesUrl, 'validate-tag-name');
        return this._httpClient.get<boolean>(`${url}?tagId=${id}&name=${encodeURIComponent(name)}`);
    }

    validateTicketCategoryName(
        categoryId: number | null,
        parentCategoryId: number | null,
        name: string
    ): Observable<boolean> {
        const url = `${this._coreApiPath}/categories/validate-category-name`;
        const categoryParam = categoryId ? `categoryId=${categoryId}&` : '';
        const parentCategoryParam = parentCategoryId ? `parentCategoryId=${parentCategoryId}&` : '';
        const nameParam = `name=${encodeURIComponent(name)}`;
        const query = `${categoryParam}${parentCategoryParam}${nameParam}`;
        return this._httpClient.get<boolean>(`${url}?${query}`);
    }

    getCandidates(query: string, includeContractors: boolean = false): Observable<TicketCandidate[]> {
        return this._httpClient.get<TicketCandidate[]>(
            `${this._coreApiPath}/candidates?includeSubcontractors=${includeContractors}&query=${query ?? ''}`
        );
    }

    getCandidateContracts(id: number): Observable<TicketCandidateContract[]> {
        return this._httpClient.get<TicketCandidateContract[]>(`${this._coreApiPath}/candidates/${id}/contracts`);
    }

    updateContractStageCode(data: unknown): Observable<unknown> {
        return this._httpClient.put(`${this._coreApiUrl}ayanova/contracts`, data);
    }

    getTicketAttachedDocs(ticketId: number): Observable<TicketAttachments[]> {
        return this._httpClient.get<TicketAttachments[]>(`${this._coreApiPath}/${ticketId}/attached-docs`);
    }

    getFile(ticketId: number, id: number, isForFileViewer: boolean = false): Observable<{ file: Blob; url: string }> {
        return this._httpClient
            .get(`${this._coreApiPath}/${ticketId}/attached-docs/${id}?isForFileViewer=${isForFileViewer}`, {
                responseType: 'blob'
            })
            .pipe(
                map((file) => {
                    const url = this._window.window.URL.createObjectURL(file);
                    return { file, url };
                })
            );
    }

    uploadFile(ticketId: number, file: TicketFilesUpload, attachmentId: number | null = null): Observable<unknown> {
        const url = `${this._coreApiPath}/${ticketId}/${
            !attachmentId ? 'attached-docs' : `attached-docs/${attachmentId}`
        }`;
        const data = new FormData();
        data.append('file', file.file);
        data.append('note', file.note || '');
        if (file.type) {
            data.append('type', file.type.toString());
        }

        return this._httpClient.post<unknown>(url, data);
    }

    deleteAttachment(ticketId: number, attachmentId: number): Observable<unknown> {
        const url = `${this._coreApiPath}/${ticketId}/attached-docs/${attachmentId}`;

        return this._httpClient.delete<unknown>(url);
    }

    removeCancellation(id: number): Observable<unknown> {
        return this._httpClient.delete(`${this._coreApiUrl}/ayanova/contracts/${id}/cancellation`);
    }

    getTicketTaskNoteHistory(ticketId: number): Observable<TicketTaskItemNoteHistory[]> {
        return this._httpClient.get<TicketTaskItemNoteHistory[]>(`${this._coreApiPath}/${ticketId}/notes-history`).pipe(
            map((notes) => {
                notes = notes.sort((first, second) => {
                    const dateA = new Date(first.createdOn).getTime();
                    const dateB = new Date(second.createdOn).getTime();

                    return dateA > dateB ? -1 : 1;
                });

                return notes;
            })
        );
    }

    searchEmployees(query: string): Observable<TicketEmployee[]> {
        const url = `${this._coreApiPath}/employees`;
        return this._httpClient.get<TicketEmployee[]>(`${url}?query=${query}`);
    }

    createTicketCategory(
        categoryTypeId: number,
        defaultAssigneeId: number | null,
        name: string,
        parentCategoryId: number | null,
        isAutoLock: boolean
    ): Observable<number> {
        const ticketCategoryParams = {
            categoryTypeId,
            defaultAssigneeId,
            name,
            parentCategoryId,
            isAutoLock
        };

        return this._httpClient.post<number>(`${this._coreApiPath}/categories`, ticketCategoryParams);
    }

    editTicketCategory(
        id: number,
        categoryTypeId: number,
        defaultAssigneeId: number | null,
        name: string,
        parentCategoryId: number | null,
        isAutoLock: boolean
    ): Observable<number> {
        const ticketCategoryParams = {
            id,
            categoryTypeId,
            defaultAssigneeId,
            name,
            parentCategoryId,
            isAutoLock
        };

        return this._httpClient.put<number>(`${this._coreApiPath}/categories`, ticketCategoryParams);
    }

    deleteTicketCategory(id: number) {
        return this._httpClient.delete(`${this._coreApiPath}/categories/${id}`);
    }

    createTicketTask(ticketId: number, assigneeId: number, description: string, dueDate: Date): Observable<unknown> {
        const ticketParams = {
            description,
            dueDate,
            assigneeId
        };

        return this._httpClient.post<unknown>(`${this._coreApiPath}/${ticketId}/tasks`, ticketParams);
    }

    updateTicketTask(
        ticketId: number,
        assigneeId: number,
        description: string,
        dueDate: Date,
        taskId: number
    ): Observable<unknown> {
        const ticketParams = {
            description,
            assigneeId,
            dueDate
        };

        return this._httpClient.put<unknown>(`${this._coreApiPath}/${ticketId}/tasks/${taskId}`, ticketParams);
    }

    updateTicketTaskNote(request: TaskUpdateRequest): Observable<unknown> {
        const ticketParams = {
            assigneeId: request.assigneeId,
            statusId: request.statusId,
            resolution: request.resolution,
            description: request.description,
            dueDate: request.dueDate
        };

        return this._httpClient.put<unknown>(
            `${this._coreApiPath}/${request.ticketId}/tasks/${request.taskId}`,
            ticketParams
        );
    }

    reassignTickets(assigneeId: number, ticketIds: number[]): Observable<unknown> {
        return this._httpClient.put<unknown>(`${this._coreApiPath}/reassign/${assigneeId}`, ticketIds);
    }

    getCategoryTypes() {
        return this._httpClient.get<TicketSettingsCategoryType[]>(`${this._coreApiPath}/categories/types`);
    }

    ticketCreate(ticket: TicketCreate): Observable<number[]> {
        return this._httpClient.post<number[]>(`${this._coreApiPath}`, ticket);
    }

    getUserTicketPermissions(): Partial<TicketGridPermissions> {
        return {
            addTicket: this._authorizedForAdd,
            openSettings: this._authorizedForSettings,
            openDetails: true,
            reassignTicket: this._authorizedForReassign
        };
    }

    getUserTicketPermission(row: TicketDetails, permission: string): boolean {
        if (permission === 'emailTicket') {
            return true;
        }

        if (permission === 'editTicket') {
            return !row.resolutionId;
        }

        if (permission === 'closeTicket') {
            return !row.resolutionId && this._authorizedForResolve;
        }

        if (permission === 'reopenTicket') {
            return !!row.resolutionId && this._authorizedForResolve;
        }

        if (permission === 'reassignTicket') {
            return !row.resolutionId && this._authorizedForReassign;
        }

        return false;
    }

    toggleLockStatus(ticketId: number): Observable<unknown> {
        return this._httpClient.put<unknown>(`${this._coreApiPath}/${ticketId}/toggleLockStatus`, null);
    }

    createStaffingAdvisoryFromTicket(data: StaffingAdvisoryFromTicketRequest) {
        return this._httpClient.post<number>(`${this._coreApiPath}/staffingadvisory`, data);
    }
}
